<template>
  <ContentContainer :loadingStatus="isLoading">
    <div class="promotion content-wrapper">
      <div class="page-header">{{ $t('editPromotion') }}</div>
      <div style="margin-bottom: 40px; margin-left: 5px;">
        <el-breadcrumb separator="/">
          <el-breadcrumb-item :to="{ name: 'HotelPromotionList' }">{{ $t('promotion') }}</el-breadcrumb-item>
          <el-breadcrumb-item>{{ $t('editPromotion') }}</el-breadcrumb-item>
        </el-breadcrumb>
      </div>
      <el-form ref="form" label-width="150px" style="width: 700px">
        <el-form-item :label="$t('code')" :required="true">
          <el-input class="text-uppercase" v-model="promotion.code" disabled></el-input>
        </el-form-item>
        <el-form-item :label="$t('detail')">
          <el-input v-model="promotion.detail" type="textarea"></el-input>
        </el-form-item>
        <el-form-item :label="$t('minNight')">
          <el-input v-model="promotion.minNight" type="number"></el-input>
        </el-form-item>
        <el-form-item :label="$t('maxNight')">
          <el-input v-model="promotion.maxNight" type="number"></el-input>
        </el-form-item>
        <el-form-item :label="$t('periodOfStay')">
          <el-row v-for="(period, index) in periodList" :key="index">
            <label v-if="index > 0" class="label-remove-period">
              <i class="el-icon-remove" style="color: red" @click="onRemovePeriodButtonClick(index)"></i>
            </label>
            <el-col :span="24">
              <el-date-picker size="small"
                              v-model="periodList[index]"
                              type="daterange"
                              format="d MMM yyyy"
                              value-format="yyyy-MM-dd"
                              start-placeholder="From"
                              end-placeholder="To"
                              @change="onDatePickerChange(index)">
              </el-date-picker>
            </el-col>
          </el-row>
          <el-row>
            <el-col :span="24">
              <el-button plain
                         type="primary"
                         @click="onAddPeriodButtonClick()"
                         size="mini"
                         style="width: 100%; max-width: 350px"><i class="el-icon-plus"></i> {{ $t('addPeriod') }} </el-button>
            </el-col>
          </el-row>
        </el-form-item>
        <el-form-item :label="$t('applyRoomType')">
          <el-row>
            <el-col :span="24">
              <apply-room-type-component
              :room-type-list="roomTypeList"
              :selected-room-type-id-list="promotion.roomTypeIdList"
              @onSelectRoomTypeChange="onApplyRoomTypeChange">
              </apply-room-type-component>
            </el-col>
          </el-row>
        </el-form-item>
        <el-form-item :label="$t('status')">
          <el-radio-group v-model="promotion.isEnable" fill="#ff0000" >
            <el-radio :label="STATUS.ENABLE">{{ $t('STATUS.ENABLE') }}</el-radio>
            <el-radio :label="STATUS.DISABLE">{{ $t('STATUS.DISABLE') }}</el-radio>
          </el-radio-group>
        </el-form-item>
      </el-form>
      <el-form ref="form" label-width="150px" style="width: 500px; margin-top: 40px">
        <el-form-item>
          <el-button type="primary" @click="onUpdatePromotionButtonClick">{{ $t('updatePromotion') }}</el-button>
          <el-button type="default" @click="onCancelButtonClick">{{ $t('cancel') }}</el-button>
        </el-form-item>
      </el-form>
    </div>
  </ContentContainer>
</template>

<script>
import { STATUS } from '@/configs/constant'
import i18nMessages from '@/translations/Hotel/ManageHotel/Promotion/Edit.i18n'
import AlertViewMixin from '@/mixins/alert-view-mixin'
import LoadingViewMixin from '@/mixins/loading-view-mixin'
import NotificationMixin from '@/mixins/notification-mixin'
import ContentContainer from '@/components/ContentContainerComponent'
import HotelPromotionApi from '@/api/hotel/promotion-api'
import LocalStorageFactory from '@/factories/local-storage-factory'
import { getHotelIndexRoute } from '@/services/hotel-service'
import { getISODateFormat, periodOverlap, getValidPeriodList, preparePeriodList } from '@/helpers/index'
import { mapState } from 'vuex'
import { get } from 'lodash'
import ApplyRoomTypeComponent from './components/ApplyRoomTypeComponent'

export default {
  name: 'HotelPromotionEdit',
  mixins: [AlertViewMixin, LoadingViewMixin, NotificationMixin],
  components: { ContentContainer, ApplyRoomTypeComponent },
  i18n: { messages: i18nMessages },
  props: {
    promotion: {
      type: Object,
      default: function () {
        return {
          id: '',
          code: '',
          detail: '',
          isEnable: STATUS.ENABLE,
          minNight: 1,
          maxNight: 1,
          periodOfStayList: [],
          roomTypeIdList: undefined
        }
      }
    },
    roomTypeList: {
      type: Array,
      default: () => {
        return []
      }
    }
  },
  data () {
    return {
      isLoading: false,
      STATUS,
      periodList: [''],
      selectedRoomTypeIdList: undefined,
      deletedPromotionPeriod: []
    }
  },
  created: async function () {
    this.isLoading = true

    if (this.activeHotelId === undefined || this.activeHotelId === '') {
      const companyDetail = LocalStorageFactory.getCompanyDetail()
      const routeName = await getHotelIndexRoute(companyDetail, this.$store)

      return this.$router.push({
        name: routeName
      })
    }

    if (this.promotion.id === undefined || this.promotion.id === '') {
      return this.$router.push({
        name: 'HotelPromotionList'
      })
    }

    this.getPeriodList()
    this.initSelectedRoomTypeIdList()
    this.isLoading = false
  },
  computed: {
    ...mapState({
      activeHotelId: state => state.general.activeHotelId
    })
  },
  methods: {
    initSelectedRoomTypeIdList: function () {
      const roomTypeIdList = get(this.promotion, 'roomTypeIdList', undefined)
      this.selectedRoomTypeIdList = roomTypeIdList
    },
    onUpdatePromotionButtonClick: async function () {
      try {
        this.startLoading()

        const periodList = getValidPeriodList(this.periodList)
        const periodOfStayData = preparePeriodList(periodList)
        const periodOfStayList = {
          periodOfStayList: periodOfStayData
        }
        const roomTypeIdList = {
          roomTypeIdList: this.selectedRoomTypeIdList
        }

        this.promotion.code = this.promotion.code.toUpperCase()
        const data = Object.assign({}, this.promotion, periodOfStayList, roomTypeIdList)
        await HotelPromotionApi.update(this.activeHotelId, this.promotion.id, data)
        await HotelPromotionApi.removePeriod(this.activeHotelId, this.promotion.id, this.deletedPromotionPeriod)

        const title = this.$t('editPromotionSuccessTitle')
        const message = this.$t('editPromotionSuccessMessage')

        this.showNotificationSuccess(title, message)
        this.$router.push({
          name: 'HotelPromotionList'
        })
      } catch (err) {
        this.alertError(err.message)
      }

      this.stopLoading()
    },
    onAddPeriodButtonClick: function () {
      this.periodList.push('')
    },
    onRemovePeriodButtonClick: function (index) {
      this.deletedPromotionPeriod.push(this.periodList[index])
      this.periodList.splice(index, 1)
    },
    getPeriodList: function () {
      const periodOfStayList = get(this.promotion, 'periodOfStayList', [])

      this.periodList = periodOfStayList.map((periodOfStay) => {
        const periodStart = get(periodOfStay, 'startDate', '')
        const periodEnd = get(periodOfStay, 'endDate', '')
        const startDate = (periodStart === '') ? periodStart : getISODateFormat(new Date(periodStart))
        const endDate = (periodEnd === '') ? periodEnd : getISODateFormat(new Date(periodEnd))
        const period = [
          startDate,
          endDate
        ]

        return period
      })

      if (this.periodList.length < 1) {
        this.periodList.push('')
      }
    },
    onCancelButtonClick: function () {
      this.$router.push({
        name: 'HotelPromotionList'
      })
    },
    onDatePickerChange: function (index) {
      const periodList = getValidPeriodList(this.periodList)
      const newPeriodList = preparePeriodList(periodList)

      if (periodOverlap(newPeriodList) === true) {
        const message = this.$t('alertOverlap')
        this.alertError(message)
        this.periodList[index][0] = ''
        this.periodList[index][1] = ''
      }
    },
    onApplyRoomTypeChange: function (selectedRoomTypeId) {
      if (!selectedRoomTypeId) {
        this.selectedRoomTypeIdList = undefined
      } else {
        this.selectedRoomTypeIdList = [...selectedRoomTypeId]
      }
    }
  }
}
</script>

<style scoped>
  .el-form > div {
    text-align: left;
  }

  .label-remove-period {
    position: absolute;
    left: -25px;
    cursor: pointer
  }
</style>
